// Entry component for /me/:agentId.
// Reads agentId from path, checks sessionStorage for a saved session token,
// shows PhoneGate if no valid session, then loads and renders the dashboard.

import { useState, useEffect } from 'react'
import { loadDashboard } from '../lib/api'
import type { DashboardData } from '../lib/types'
import { getStrings } from '../lib/i18n'
import { kgToLb } from '../lib/units'
import { PhoneGate } from './PhoneGate'
import { UserHeader } from './UserHeader'
import { ProgressGauge } from './ProgressGauge'
import { TrendChart } from './TrendChart'
import { TargetHistory } from './TargetHistory'
import { WeeklyReports } from './WeeklyReports'
import { DateNav } from './DateNav'
import { DayLog } from './DayLog'
import { ExerciseCard } from './ExerciseCard'

function sessionKey(agentId: string) {
  return `dashboard_session_${agentId}`
}

function phoneKey(agentId: string) {
  return `dashboard_phone_${agentId}`
}

// Read a `?phone=` query param (operator links from an internal admin tool) or a
// `?key=` access-key param (coach-shared links for phone-less channels — Apple
// Messages; the worker verifies either through the same endpoint), then strip it
// from the URL so the secret isn't left in the address bar / history / a
// shareable URL. Returns the URL-decoded value, or null.
// Runs once at module load (before render) so the gate never flashes the empty form.
function consumePhoneParam(): string | null {
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('phone') ?? params.get('key')
    if (raw === null) return null
    // Strip just the secret keys, keep the /me/:agentId path + any other params.
    params.delete('phone')
    params.delete('key')
    const qs = params.toString()
    const newUrl = window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
    window.history.replaceState(null, '', newUrl)
    // URLSearchParams already URL-decodes; return null for an empty value.
    const phone = raw.trim()
    return phone || null
  } catch {
    return null
  }
}

function readStoredSession(agentId: string): string | null {
  try {
    // localStorage survives browser restarts within the 24h server-side expiry window.
    return localStorage.getItem(sessionKey(agentId)) ?? sessionStorage.getItem(sessionKey(agentId))
  } catch {
    return null
  }
}

function readStoredPhone(agentId: string): string | null {
  try {
    // localStorage persists across sessions/tabs; sessionStorage is the legacy fallback.
    return localStorage.getItem(phoneKey(agentId)) ?? sessionStorage.getItem(phoneKey(agentId))
  } catch {
    return null
  }
}

interface Props {
  agentId: string
}

type LoadState = 'gate' | 'loading' | 'loaded' | 'error'

export function DashboardApp({ agentId }: Props) {
  // Read + strip `?phone=` exactly once on mount (lazy initializer). Even if a
  // valid stored session means we never show the gate, the param is still stripped
  // from the URL so the phone secret doesn't linger in history/the address bar.
  const [autoPhone] = useState<string | null>(consumePhoneParam)
  const [session, setSession] = useState<string | null>(readStoredSession(agentId))
  // Phone entered at the gate — used for header name fallback.
  const [phone, setPhone] = useState<string | null>(readStoredPhone(agentId))
  const [data, setData] = useState<DashboardData | null>(null)
  const [loadState, setLoadState] = useState<LoadState>(session ? 'loading' : 'gate')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [date, setDate] = useState<string | undefined>(undefined)
  // Date-switch refetches keep the dashboard mounted (no full-page spinner) — SPA, no reload.
  const [dayLoading, setDayLoading] = useState(false)

  // Fetch dashboard whenever session or date changes.
  useEffect(() => {
    if (!session) return
    // Full-page spinner only on the very first load; date switches update in place.
    if (data === null) setLoadState('loading')
    else setDayLoading(true)
    setErrorMsg(null)
    loadDashboard(agentId, session, date)
      .then((d) => {
        setData(d)
        setLoadState('loaded')
        setDayLoading(false)
      })
      .catch((err: Error) => {
        setDayLoading(false)
        if (err.message === 'unauthorized') {
          // Session expired or invalid — drop it from both stores and re-gate.
          try {
            localStorage.removeItem(sessionKey(agentId))
            sessionStorage.removeItem(sessionKey(agentId))
          } catch { /* ignore */ }
          setSession(null)
          setLoadState('gate')
        } else {
          setErrorMsg(err.message)
          setLoadState('error')
        }
      })
  }, [agentId, session, date])

  // Browser tab title: who this is + their headline progress, so a pinned/background
  // tab is readable without opening it. Falls back to the agentId when the profile has
  // no name yet. Never put the phone here — it is this page's only auth secret, and a
  // title is written into browser history (consumePhoneParam already strips it from the
  // URL for exactly that reason).
  useEffect(() => {
    if (!data) return
    const who = data.profile.name?.trim() || agentId
    const lb = kgToLb(data.progress.changed_kg)
    const verb = data.progress.direction === 'gain' ? 'gained' : 'lost'
    document.title =
      lb >= 0.1
        ? `${who} · ${lb.toFixed(1)} lb ${verb} — NanoRhino`
        : `${who} — NanoRhino`
  }, [data, agentId])

  // English only (owner directive 2026-07-12) — the language toggle was removed.
  const s = getStrings()

  if (loadState === 'gate') {
    return (
      <PhoneGate
        agentId={agentId}
        strings={s}
        autoPhone={autoPhone}
        onVerified={(tok, enteredPhone) => {
          try {
            localStorage.setItem(sessionKey(agentId), tok)
            sessionStorage.setItem(sessionKey(agentId), tok)
            localStorage.setItem(phoneKey(agentId), enteredPhone)
            sessionStorage.setItem(phoneKey(agentId), enteredPhone)
          } catch { /* ignore */ }
          setPhone(enteredPhone)
          setSession(tok)
          setLoadState('loading')
        }}
      />
    )
  }

  if (loadState === 'loading') {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" aria-label={s.loading} />
        <p>{s.loading}</p>
      </div>
    )
  }

  if (loadState === 'error' || !data) {
    return (
      <div className="dash-error">
        <p>{errorMsg ?? s.loadError}</p>
        <button
          type="button"
          className="dash-btn"
          onClick={() => {
            setLoadState('loading')
            setErrorMsg(null)
          }}
        >
          {s.retry}
        </button>
      </div>
    )
  }

  // Exercise is a sparse, newer feature — guard against older cached API
  // responses that predate the field, and only show the card when the user
  // has any exercise data at all (series history OR the selected day's log).
  const exerciseSeries = data.exercise_series ?? []
  const dayExercise = data.day.exercise ?? { total_calories: 0, entries: [] }
  const hasExercise = exerciseSeries.length > 0 || dayExercise.entries.length > 0

  // Weekly reports — newer field, absent from older cached responses.
  const weeklyReports = data.weekly_reports ?? []

  // Closed-loop calorie target — newer fields, and sparse even once deployed:
  // only users on the controller (plan schema 2) have them. TargetHistory renders
  // nothing at all when both are empty.
  const targetHistory = data.target_history ?? []
  const loop = data.loop ?? null

  return (
    <div className="dash-app">
      <UserHeader
        profile={data.profile}
        targets={data.targets}
        calorieSeries={data.calorie_series}
        phone={phone}
        strings={s}
      />
      <ProgressGauge progress={data.progress} strings={s} />
      <TrendChart
        weightSeries={data.weight_series}
        calorieSeries={data.calorie_series}
        targets={data.targets}
        targetWeightKg={data.progress.target_kg}
        strings={s}
      />
      <TargetHistory history={targetHistory} loop={loop} strings={s} />
      <WeeklyReports agentId={agentId} reports={weeklyReports} strings={s} />
      {/* Shared date header — governs BOTH the Food log and Exercise cards below. */}
      <DateNav
        date={data.day.date}
        calorieSeries={data.calorie_series}
        dailyCalTarget={data.targets.daily_calories}
        strings={s}
        onDateChange={(d) => setDate(d)}
      />
      <div
        className="dash-daylog-wrap"
        aria-busy={dayLoading}
        style={{ opacity: dayLoading ? 0.55 : 1, transition: 'opacity 0.15s ease' }}
      >
        <DayLog day={data.day} targets={data.targets} strings={s} />
        {hasExercise && (
          <ExerciseCard day={{ ...data.day, exercise: dayExercise }} strings={s} />
        )}
      </div>
    </div>
  )
}
